import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Stack, Chip, Avatar, Skeleton, Button } from '@mui/material';
import { Assignment, Person, Schedule, Article, ChatBubble, Visibility, Roofing } from '@mui/icons-material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';

import { Application, Post } from '@libs/models';
import {
  APPLICATION_INDEX_COLUMN_INITIAL_STATE,
  createApplicationIndexColumns,
  routes,
  useIsMobile,
} from '@carcareers/utils';
import { useChat, useSnackbar } from '@carcareers/contexts';
import { DashboardPageContainer, PostStatusChip, DataGridV2 as DataGrid } from '@carcareers/components';
import { postApi, usePostApplicationsSearchQuery } from '@carcareers/store';

export const EmployerPostView = () => {
  const { toggleSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const postId = id ? parseInt(id) : undefined;
  const { data, isLoading } = postApi.usePostViewQuery(postId ?? 0, { skip: !postId });
  const [post, setPost] = useState<Post>();
  const search = usePostApplicationsSearchQuery;
  const { employerChatAction } = useChat();
  const isMobile = useIsMobile();

  const applications = post?.applications || [];

  const getJobInitials = (jobTitle?: string) => {
    if (!jobTitle) return 'J';
    return jobTitle
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getDaysLeft = () => {
    if (!post?.endDate) return 0;
    const endDate = new Date(post.endDate);
    const now = new Date();
    if (endDate <= now) return 0;
    return Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const columns = useMemo(() => {
    const cols = createApplicationIndexColumns(false).filter((col) => col.field !== 'source');
    cols.push({
      field: 'actions',
      type: 'actions' as const,
      flex: 1,
      align: 'right' as const,
      headerAlign: 'right' as const,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          showInMenu
          key={`view-${params.row.id}`}
          label="View"
          icon={<Visibility />}
          onClick={() => {
            if (params.row.id) {
              navigate(
                `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_APPLICATIONS_VIEW.replace(
                  ':id',
                  postId?.toString() || '',
                ).replace(':applicationId', params.row.id?.toString() || '')}`,
              );
            }
          }}
        />,
        <GridActionsCellItem
          showInMenu
          disabled={params.row.jobseeker?.isExternalApply}
          key={`chat-${params.row.id}`}
          label="Chat"
          icon={<ChatBubble />}
          onClick={async () => {
            const row = params.row as Application;
            if (row?.jobseeker?.user?.id && post?.id && post?.job?.title) {
              await employerChatAction(
                post?.id,
                row.jobseeker?.user?.id,
                row.jobseeker?.user?.firstName + ' ' + row.jobseeker?.user?.lastName,
                post?.job?.title,
                post?.job?.rooftop?.name,
              );
            } else {
              toggleSnackbar('error', 'Failed to start chat');
            }
          }}
        />,
      ],
    });

    return cols;
  }, [postId, navigate, post, employerChatAction, toggleSnackbar]);

  useEffect(() => {
    if (!postId) {
      navigate(routes.EMPLOYER_HOME);
    }

    if (!isLoading && data) {
      setPost(data);
    }
  }, [data, postId, navigate, isLoading]);

  return (
    <DashboardPageContainer idReplacement={post?.job?.title}>
      <Stack spacing={3}>
        {/* Header Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {isLoading ? '' : getJobInitials(post?.job?.title)}
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? <Skeleton variant="text" width={300} height={32} /> : post?.job?.title || 'Untitled Job'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isLoading ? (
                    <Skeleton variant="text" width={200} height={20} />
                  ) : (
                    post?.job?.rooftop?.name || 'Unknown Rooftop'
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  {isLoading ? (
                    <Skeleton variant="rounded" width={120} height={32} />
                  ) : (
                    <Chip
                      icon={<Assignment />}
                      label={`Post ID: ${post?.id || '...'}`}
                      variant="outlined"
                      sx={{ px: 1 }}
                    />
                  )}
                  {isLoading ? (
                    <Skeleton variant="rounded" width={100} height={32} />
                  ) : (
                    post?.status && <PostStatusChip status={post.status} />
                  )}
                  {isLoading ? (
                    <Skeleton variant="rounded" width={150} height={32} />
                  ) : (
                    <Chip
                      icon={<Schedule />}
                      label={`Posted ${new Date(post?.startDate || '').toLocaleDateString()}`}
                      variant="outlined"
                      sx={{ px: 1 }}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Article />}
                  onClick={() =>
                    navigate(
                      `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_JOBS_VIEW.replace(
                        ':id',
                        post?.job?.id?.toString() || '',
                      )}`,
                    )
                  }
                  sx={{ borderRadius: 2 }}
                >
                  View Template
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Roofing />}
                  onClick={() =>
                    navigate(
                      `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_ROOFTOPS_VIEW.replace(
                        ':id',
                        post?.job?.rooftop?.id?.toString() || '',
                      )}`,
                    )
                  }
                  sx={{ borderRadius: 2 }}
                >
                  View Rooftop
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Person sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {isLoading ? (
                  <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
                ) : (
                  applications.length || 0
                )}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Applications
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'warning.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Schedule sx={{ color: 'warning.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                {isLoading ? (
                  <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
                ) : (
                  getDaysLeft()
                )}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Days Left
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Roofing sx={{ color: 'secondary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
                {isLoading ? (
                  <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
                ) : (
                  post?.job?.rooftop?.name?.substring(0, 3).toUpperCase() || 'N/A'
                )}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Rooftop
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Applications Section */}
        <Card
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ color: 'primary.main' }} />
            Applications ({applications.length})
          </Typography>
        </Card>
        <DataGrid<Application>
          parentId={postId}
          query={search}
          columns={columns}
          initialState={{
            columns: {
              columnVisibilityModel: APPLICATION_INDEX_COLUMN_INITIAL_STATE,
            },
          }}
        />
      </Stack>
    </DashboardPageContainer>
  );
};

export default EmployerPostView;
